// Web Streams API types are now available globally via DOM types in tsconfig
import type { Span } from '@opentelemetry/api';
import { randomUUID } from './crypto.js';

import { SSEParser } from './sse.js';
import { TextDecoderStreamPolyfill } from './stream.js';

// Configuration Types
export interface RetryConfig {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffFactor: number;
  retryableStatusCodes: number[];
}

export interface RequestMetrics {
  startTime: number;
  retryCount: number;
  lastRetryTime?: number;
  streamChunks?: number;
  lastChunkTime?: number;
  streamDuration?: number;
  errorTime?: number;
}

export interface AxAPIResponseMetadata {
  requestId: string;
  status: number;
  statusText: string;
  headers: Headers;
  url: string;
  retryCount: number;
}

// Validation Interfaces
interface RequestValidation {
  validateRequest?: (request: unknown) => boolean | Promise<boolean>;
}

interface ResponseValidation {
  validateResponse?: (response: unknown) => boolean | Promise<boolean>;
}

// API Base Types
export interface AxAPI {
  name?: string;
  url?: string | URL;
  headers?: Record<string, string>;
  /** HTTP method for JSON API requests. Defaults to POST. */
  method?: 'POST' | 'PUT' | 'PATCH';
  /** @deprecated Use `method: 'PUT'` instead. */
  put?: boolean;
  localCall?: <TRequest, TResponse>(
    data: TRequest,
    stream?: boolean
  ) => Promise<TResponse | ReadableStream<TResponse>>;
}

// Enhanced API Configuration
export interface AxAPIConfig
  extends AxAPI,
    RequestValidation,
    ResponseValidation {
  url?: string | URL; // Make URL optional when localCall is provided
  stream?: boolean;
  debug?: boolean; // Deprecated: use verbose instead
  verbose?: boolean; // Low-level HTTP request/response logging
  fetch?: typeof fetch;
  span?: Span;
  timeout?: number;
  retry?: Partial<RetryConfig>;
  abortSignal?: AbortSignal;
  corsProxy?: string;
  onResponseMetadata?: (metadata: Readonly<AxAPIResponseMetadata>) => void;
  /** Resolve headers for each HTTP attempt, including retries. */
  resolveHeaders?: (request: {
    method: string;
    url: string;
    retryCount: number;
  }) => Promise<Record<string, string>>;
  /** Whether to include request body in error messages. Defaults to true. Set to false when request may contain sensitive data or large base64 content. */
  includeRequestBodyInErrors?: boolean;
}

// Default Configurations
export const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 60000,
  backoffFactor: 2,
  retryableStatusCodes: [500, 408, 429, 502, 503, 504, 529],
};

const textDecoderStream =
  (globalThis as any).TextDecoderStream ?? TextDecoderStreamPolyfill;

// Error Classes
export class AxAIServiceError extends Error {
  public readonly timestamp: string;
  public readonly errorId: string;
  public readonly context: Record<string, unknown>;
  public readonly includeRequestBodyInErrors: boolean;

  constructor(
    message: string,
    public readonly url: string,
    public readonly requestBody: unknown,
    public readonly responseBody: unknown,
    context: Record<string, unknown> = {},
    includeRequestBodyInErrors: boolean = true
  ) {
    super(message);
    this.name = 'AxAIServiceError';
    this.timestamp = new Date().toISOString();
    this.errorId = randomUUID();
    this.context = context;
    this.includeRequestBodyInErrors = includeRequestBodyInErrors;

    this.stack = this.toString();
  }

  override toString(): string {
    const lines = [`${this.name}: ${this.message}`, `URL: ${this.url}`];

    if (this.includeRequestBodyInErrors) {
      lines.push(`Request Body: ${JSON.stringify(this.requestBody, null, 2)}`);
    }

    lines.push(
      `Response Body: ${JSON.stringify(this.responseBody, null, 2)}`,
      `Context: ${JSON.stringify(this.context, null, 2)}`,
      `Timestamp: ${this.timestamp}`,
      `Error ID: ${this.errorId}`
    );

    return lines.join('\n');
  }

  // For Node.js, override the custom inspect method so console.log shows our custom string.
  [Symbol.for('nodejs.util.inspect.custom')](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _depth: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _options: Record<string, unknown>
  ) {
    return this.toString();
  }
}

export class AxAIServiceStatusError extends AxAIServiceError {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    url: string,
    requestBody: unknown,
    responseBody: unknown,
    context?: Record<string, unknown>,
    retryCount?: number,
    includeRequestBodyInErrors: boolean = true
  ) {
    const retryInfo = retryCount ? ` (after ${retryCount} retries)` : '';
    super(
      `HTTP ${status} - ${statusText}${retryInfo}`,
      url,
      requestBody,
      {
        httpStatus: status,
        httpStatusText: statusText,
        responseBody,
        ...context,
      },
      {},
      includeRequestBodyInErrors
    );
    this.name = 'AxAIServiceStatusError';
  }
}

export class AxAIServiceNetworkError extends AxAIServiceError {
  constructor(
    public readonly originalError: Error,
    url: string,
    requestBody: unknown,
    responseBody: unknown,
    context?: Record<string, unknown>,
    includeRequestBodyInErrors: boolean = true
  ) {
    super(
      `Network Error: ${originalError.message}`,
      url,
      requestBody,
      responseBody,
      {
        originalErrorName: originalError.name,
        originalErrorStack: originalError.stack,
        ...context,
      },
      includeRequestBodyInErrors
    );
    this.name = 'AxAIServiceNetworkError';
    this.stack = originalError.stack;
  }
}

export class AxAIServiceResponseError extends AxAIServiceError {
  constructor(
    message: string,
    url: string,
    requestBody?: unknown,
    context?: Record<string, unknown>,
    includeRequestBodyInErrors: boolean = true
  ) {
    super(
      message,
      url,
      requestBody,
      undefined,
      context,
      includeRequestBodyInErrors
    );
    this.name = 'AxAIServiceResponseError';
  }
}

export class AxAIServiceStreamTerminatedError extends AxAIServiceError {
  constructor(
    url: string,
    requestBody?: unknown,
    public readonly lastChunk?: unknown,
    context?: Record<string, unknown>,
    includeRequestBodyInErrors: boolean = true
  ) {
    super(
      'Stream terminated unexpectedly by remote host',
      url,
      requestBody,
      undefined,
      {
        lastChunk,
        ...context,
      },
      includeRequestBodyInErrors
    );
    this.name = 'AxAIServiceStreamTerminatedError';
  }
}

export class AxAIServiceTimeoutError extends AxAIServiceError {
  constructor(
    url: string,
    timeoutMs: number,
    requestBody?: unknown,
    context?: Record<string, unknown>,
    includeRequestBodyInErrors: boolean = true
  ) {
    super(
      `Request timed out after ${timeoutMs}ms`,
      url,
      requestBody,
      undefined,
      { timeoutMs, ...context },
      includeRequestBodyInErrors
    );
    this.name = 'AxAIServiceTimeoutError';
  }
}

export class AxTokenLimitError extends AxAIServiceStatusError {
  constructor(
    status: number,
    statusText: string,
    url: string,
    requestBody: unknown,
    responseBody: unknown,
    context?: Record<string, unknown>,
    includeRequestBodyInErrors: boolean = true
  ) {
    super(
      status,
      statusText,
      url,
      requestBody,
      responseBody,
      context,
      undefined,
      includeRequestBodyInErrors
    );
    this.name = 'AxTokenLimitError';
  }
}

export class AxAIServiceAbortedError extends AxAIServiceError {
  constructor(
    url: string,
    reason?: string,
    requestBody?: unknown,
    context?: Record<string, unknown>,
    includeRequestBodyInErrors: boolean = true
  ) {
    super(
      `Request aborted${reason ? `: ${reason}` : ''}`,
      url,
      requestBody,
      undefined,
      { abortReason: reason, ...context },
      includeRequestBodyInErrors
    );
    this.name = 'AxAIServiceAbortedError';
  }
}

export class AxAIServiceAuthenticationError extends AxAIServiceError {
  constructor(
    url: string,
    requestBody: unknown,
    responseBody: unknown,
    context?: Record<string, unknown>,
    includeRequestBodyInErrors: boolean = true
  ) {
    super(
      'Authentication failed',
      url,
      requestBody,
      responseBody,
      context,
      includeRequestBodyInErrors
    );
    this.name = 'AxAIServiceAuthenticationError';
  }
}

export class AxAIRefusalError extends Error {
  public readonly timestamp: string;
  public readonly errorId: string;

  constructor(
    public readonly refusalMessage: string,
    public readonly model?: string,
    public readonly requestId?: string,
    public readonly category?: string | null,
    public readonly explanation?: string | null
  ) {
    super(`Model refused to fulfill request: ${refusalMessage}`);
    this.name = 'AxAIRefusalError';
    this.timestamp = new Date().toISOString();
    this.errorId = randomUUID();
  }

  override toString(): string {
    return [
      `${this.name}: ${this.message}`,
      `Refusal: ${this.refusalMessage}`,
      this.model ? `Model: ${this.model}` : '',
      this.requestId ? `Request ID: ${this.requestId}` : '',
      this.category ? `Category: ${this.category}` : '',
      this.explanation ? `Explanation: ${this.explanation}` : '',
      `Timestamp: ${this.timestamp}`,
      `Error ID: ${this.errorId}`,
    ]
      .filter(Boolean)
      .join('\n');
  }

  // For Node.js, override the custom inspect method so console.log shows our custom string.
  [Symbol.for('nodejs.util.inspect.custom')](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _depth: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _options: Record<string, unknown>
  ) {
    return this.toString();
  }
}

/**
 * Error thrown when an AI provider doesn't support a required media type.
 *
 * This error is thrown during content processing when a provider cannot handle
 * a specific media type and no suitable fallback mechanism is available or configured.
 *
 * @example
 * ```typescript
 * try {
 *   await textOnlyProvider.chat(imageRequest);
 * } catch (error) {
 *   if (error instanceof AxMediaNotSupportedError) {
 *     console.log(`${error.mediaType} not supported by ${error.provider}`);
 *     if (error.fallbackAvailable) {
 *       console.log('Consider using content processing services');
 *     }
 *   }
 * }
 * ```
 */
export class AxMediaNotSupportedError extends Error {
  /** ISO timestamp when the error occurred */
  public readonly timestamp: string;
  /** Unique identifier for this error instance */
  public readonly errorId: string;

  /**
   * Creates a new media not supported error.
   *
   * @param mediaType - The type of media that is not supported (e.g., 'Images', 'Audio')
   * @param provider - The name of the AI provider that doesn't support the media type
   * @param fallbackAvailable - Whether fallback processing options are available
   */
  constructor(
    public readonly mediaType: string,
    public readonly provider: string,
    public readonly fallbackAvailable: boolean = false
  ) {
    super(
      `${mediaType} not supported by ${provider}${fallbackAvailable ? ' (fallback available)' : ''}`
    );
    this.name = 'AxMediaNotSupportedError';
    this.timestamp = new Date().toISOString();
    this.errorId = randomUUID();
  }

  override toString(): string {
    return [
      `${this.name}: ${this.message}`,
      `Media Type: ${this.mediaType}`,
      `Provider: ${this.provider}`,
      `Fallback Available: ${this.fallbackAvailable}`,
      `Timestamp: ${this.timestamp}`,
      `Error ID: ${this.errorId}`,
    ].join('\n');
  }

  // For Node.js, override the custom inspect method so console.log shows our custom string.
  [Symbol.for('nodejs.util.inspect.custom')](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _depth: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _options: Record<string, unknown>
  ) {
    return this.toString();
  }
}

/**
 * Error thrown when content processing/transformation fails.
 *
 * This error wraps underlying failures from content processing services like
 * image-to-text, audio transcription, file text extraction, or URL content fetching.
 * It provides context about what type of content was being processed and at which step.
 *
 * @example
 * ```typescript
 * try {
 *   await axProcessContentForProvider(content, provider, {
 *     imageToText: imageService.analyze
 *   });
 * } catch (error) {
 *   if (error instanceof AxContentProcessingError) {
 *     console.log(`Failed processing ${error.contentType} during ${error.processingStep}`);
 *     console.log('Original error:', error.originalError.message);
 *   }
 * }
 * ```
 */
export class AxContentProcessingError extends Error {
  /** ISO timestamp when the error occurred */
  public readonly timestamp: string;
  /** Unique identifier for this error instance */
  public readonly errorId: string;

  /**
   * Creates a new content processing error.
   *
   * @param originalError - The underlying error that caused the processing failure
   * @param contentType - The type of content being processed (e.g., 'image', 'audio', 'file')
   * @param processingStep - The specific processing step that failed (e.g., 'vision analysis', 'transcription')
   */
  constructor(
    public readonly originalError: Error,
    public readonly contentType: string,
    public readonly processingStep: string
  ) {
    super(
      `Failed to process ${contentType} during ${processingStep}: ${originalError.message}`
    );
    this.name = 'AxContentProcessingError';
    this.timestamp = new Date().toISOString();
    this.errorId = randomUUID();
  }

  override toString(): string {
    return [
      `${this.name}: ${this.message}`,
      `Content Type: ${this.contentType}`,
      `Processing Step: ${this.processingStep}`,
      `Original Error: ${this.originalError.message}`,
      `Timestamp: ${this.timestamp}`,
      `Error ID: ${this.errorId}`,
    ].join('\n');
  }

  // For Node.js, override the custom inspect method so console.log shows our custom string.
  [Symbol.for('nodejs.util.inspect.custom')](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _depth: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _options: Record<string, unknown>
  ) {
    return this.toString();
  }
}

// Utility Functions
// Header names whose values carry credentials and must never be logged.
const SENSITIVE_HEADER_NAMES = new Set([
  'authorization',
  'proxy-authorization',
  'x-api-key',
  'api-key',
  'apikey',
  'x-goog-api-key',
  'x-amz-security-token',
  'cookie',
  'set-cookie',
]);

/**
 * Return a shallow copy of `headers` with the values of sensitive keys masked,
 * for safe logging. Header-name matching is case-insensitive. The original
 * headers object is never mutated, so the real credentials are still sent.
 *
 * Sensitive values are masked in full. Preserving a prefix is unsafe for
 * headers such as Cookie, whose first value commonly appears before a space.
 */
function redactHeaders(
  headers: Record<string, string>
): Record<string, string> {
  const redacted: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (SENSITIVE_HEADER_NAMES.has(key.toLowerCase())) {
      redacted[key] = '***';
    } else {
      redacted[key] = value;
    }
  }
  return redacted;
}

async function safeReadResponseBody(response: Response) {
  try {
    if (response.headers.get('content-type')?.includes('application/json')) {
      return await response.json();
    }

    // Clone the response so we can read it without consuming the original
    const clonedResponse = response.clone();
    return await clonedResponse.text();
  } catch (e) {
    // If we can't read the body, return a descriptive message
    return `[ReadableStream - read failed: ${(e as Error).message}]`;
  }
}

function calculateRetryDelay(
  attempt: number,
  config: Readonly<RetryConfig>
): number {
  const delay = Math.min(
    config.maxDelayMs,
    config.initialDelayMs * config.backoffFactor ** attempt
  );
  return delay * (0.75 + Math.random() * 0.5);
}

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;

  // Try parsing as integer (seconds)
  const seconds = Number(header);
  if (!Number.isNaN(seconds)) {
    return seconds * 1000;
  }

  // Try parsing as HTTP Date
  const date = Date.parse(header);
  if (!Number.isNaN(date)) {
    const diff = date - Date.now();
    return Math.max(0, diff);
  }

  return undefined;
}

function createRequestMetrics(): RequestMetrics {
  return {
    startTime: Date.now(),
    retryCount: 0,
  };
}

// eslint-disable-next-line functional/prefer-immutable-types
function updateRetryMetrics(metrics: RequestMetrics): void {
  metrics.retryCount++;
  metrics.lastRetryTime = Date.now();
}

function shouldRetry(
  error: Error,
  status: number | undefined,
  attempt: number,
  config: Readonly<RetryConfig>
): boolean {
  if (attempt >= config.maxRetries) return false;
  if (status && config.retryableStatusCodes.includes(status)) return true;

  return (
    error instanceof AxAIServiceNetworkError &&
    !(error instanceof AxAIServiceAuthenticationError)
  );
}

// Enhanced API Call Function
export const apiCall = async <TRequest = unknown, TResponse = unknown>(
  api: Readonly<AxAPIConfig>,
  json: TRequest
): Promise<TResponse | ReadableStream<TResponse>> => {
  // If localCall is provided, use it instead of HTTP
  if (api.localCall) {
    return await api.localCall<TRequest, TResponse>(json, api.stream);
  }

  // Ensure URL is provided for HTTP calls
  if (!api.url) {
    throw new Error('API URL is required when localCall is not provided');
  }

  const retryConfig: RetryConfig = { ...defaultRetryConfig, ...api.retry };
  const timeoutMs = api.timeout;
  const metrics = createRequestMetrics();
  // Only verbose flag controls HTTP request/response logging
  const verbose = api.verbose ?? false;
  const includeBodyInErrors = api.includeRequestBodyInErrors ?? true;
  const method = api.method ?? (api.put ? 'PUT' : 'POST');
  let timeoutId: NodeJS.Timeout | undefined;

  const baseUrl = new URL(api.url);
  const apiPath = `${[baseUrl.pathname, api.name]
    .filter(Boolean)
    .join('/')
    .replace(/\/+/g, '/')}${baseUrl.search}`;
  let apiUrl = new URL(apiPath, baseUrl);

  // Apply CORS proxy if provided (for browser environments)
  if (api.corsProxy) {
    const originalUrl = apiUrl.href;
    apiUrl = new URL(`${api.corsProxy}?url=${encodeURIComponent(originalUrl)}`);
  }

  const requestId = randomUUID();

  // Validate request if validator is provided
  if (api.validateRequest) {
    const isValid = await api.validateRequest(json);
    if (!isValid) {
      throw new AxAIServiceResponseError(
        'Invalid request data',
        apiUrl.href,
        json,
        { validation: 'request' },
        includeBodyInErrors
      );
    }
  }

  // Set up telemetry
  api.span?.setAttributes({
    'http.request.method': method,
    'url.full': apiUrl.href,
    'request.id': requestId,
    'request.startTime': metrics.startTime,
  });

  let attempt = 0;

  while (true) {
    // Credential/header provider failures are configuration failures. Resolve
    // outside the transport try/catch so they abort without network retries.
    const requestHeaders = api.resolveHeaders
      ? await api.resolveHeaders({
          method,
          url: apiUrl.href,
          retryCount: attempt,
        })
      : api.headers;
    // Combine user abort signal with timeout signal
    const combinedAbortController = new AbortController();

    // Idempotent removal of the user abort listener. Defined per attempt so
    // every exit path (success, non-abort error, and each retry `continue`)
    // can detach the listener it registered on the long-lived caller signal.
    // `removeEventListener` is a no-op when the listener is already gone, so
    // calling this more than once is safe.
    let removeUserAbortHandler: (() => void) | undefined;

    // The streaming response is consumed after this function returns, so the
    // listener must outlive the outer `finally` and is instead detached by the
    // stream's own teardown (its `finally`/`cancel`). This flag tells the outer
    // `finally` to leave the listener in place for that case only.
    let abortHandlerOwnedByStream = false;

    // Handle user abort signal
    if (api.abortSignal) {
      if (api.abortSignal.aborted) {
        throw new AxAIServiceAbortedError(
          apiUrl.href,
          api.abortSignal.reason,
          json,
          { metrics },
          includeBodyInErrors
        );
      }

      const userAbortHandler = () => {
        combinedAbortController.abort(
          api.abortSignal!.reason || 'User aborted request'
        );
      };
      api.abortSignal.addEventListener('abort', userAbortHandler, {
        once: true,
      });
      removeUserAbortHandler = () => {
        api.abortSignal!.removeEventListener('abort', userAbortHandler);
      };

      // Clean up listener if we complete before abort
      const originalAbort = combinedAbortController.abort.bind(
        combinedAbortController
      );
      combinedAbortController.abort = (reason?: string) => {
        removeUserAbortHandler?.();
        originalAbort(reason);
      };
    }

    if (timeoutMs) {
      timeoutId = setTimeout(() => {
        combinedAbortController.abort('Request timeout');
      }, timeoutMs);
    }

    try {
      // Set up timeout with proper cleanup

      if (verbose) {
        console.log(
          '\n--- [AxAI API Request] ---\n',
          `URL: ${apiUrl.href}\n`,
          `Method: ${method}\n`,
          `Headers:`,
          JSON.stringify(
            redactHeaders({
              'Content-Type': 'application/json',
              'X-Request-ID': requestId,
              'X-Retry-Count': attempt.toString(),
              ...requestHeaders,
            }),
            null,
            2
          ),
          '\nBody:',
          JSON.stringify(json, null, 2),
          '\n------------------------\n'
        );
      }

      const res = await (api.fetch ?? fetch)(apiUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': requestId,
          'X-Retry-Count': attempt.toString(),
          ...requestHeaders,
        },
        body: JSON.stringify(json),
        signal: combinedAbortController.signal,
      });

      api.onResponseMetadata?.({
        requestId,
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
        url: apiUrl.href,
        retryCount: metrics.retryCount,
      });

      let errorResponseBody: unknown;
      let errorResponseBodyRead = false;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Handle authentication errors
      if (res.status === 401 || res.status === 403) {
        const responseBody = await safeReadResponseBody(res);
        throw new AxAIServiceAuthenticationError(
          apiUrl.href,
          json,
          responseBody,
          {
            metrics,
          },
          includeBodyInErrors
        );
      }

      // Handle token limit errors (400 Bad Request)
      if (res.status === 400) {
        errorResponseBody = await safeReadResponseBody(res);
        errorResponseBodyRead = true;
        const error = errorResponseBody as any;
        let isTokenLimit = false;

        // OpenAI / Azure OpenAI and compatible endpoints
        if (error?.error?.code === 'context_length_exceeded') {
          isTokenLimit = true;
        }
        // Anthropic
        else if (
          error?.type === 'invalid_request_error' &&
          (error?.error?.message?.includes('prompt is too long') ||
            error?.error?.message?.includes('max_tokens') ||
            error?.error?.message?.includes('token limit'))
        ) {
          isTokenLimit = true;
        }
        // Google Gemini (typically 400 with INVALID_ARGUMENT)
        else if (
          error?.error?.code === 400 &&
          error?.error?.status === 'INVALID_ARGUMENT' &&
          (error?.error?.message?.includes('token') ||
            error?.error?.message?.includes('limit'))
        ) {
          isTokenLimit = true;
        }
        // Generic check for "token" and "limit" or "context length" in message/body
        else {
          const bodyString = (
            JSON.stringify(errorResponseBody) ?? ''
          ).toLowerCase();
          if (
            (bodyString.includes('token') && bodyString.includes('limit')) ||
            bodyString.includes('context length') ||
            bodyString.includes('prompt is too long')
          ) {
            isTokenLimit = true;
          }
        }

        if (isTokenLimit) {
          throw new AxTokenLimitError(
            res.status,
            res.statusText,
            apiUrl.href,
            json,
            errorResponseBody,
            {
              metrics,
            },
            includeBodyInErrors
          );
        }
      }

      // Handle retryable status codes
      if (
        res.status >= 400 &&
        shouldRetry(new Error(), res.status, attempt, retryConfig)
      ) {
        let delay = calculateRetryDelay(attempt, retryConfig);

        const retryAfterMs = parseRetryAfter(res.headers.get('Retry-After'));
        if (
          retryAfterMs !== undefined &&
          retryAfterMs <= retryConfig.maxDelayMs
        ) {
          delay = retryAfterMs;
          if (verbose) {
            console.log(`[AxAI] Respecting Retry-After header: ${delay}ms`);
          }
        }
        attempt++;
        updateRetryMetrics(metrics);

        api.span?.addEvent('retry', {
          attempt,
          delay,
          status: res.status,
          'metrics.startTime': metrics.startTime,
          'metrics.retryCount': metrics.retryCount,
          'metrics.lastRetryTime': metrics.lastRetryTime,
        });

        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      if (res.status >= 400) {
        if (!errorResponseBodyRead) {
          errorResponseBody = await safeReadResponseBody(res);
        }
        throw new AxAIServiceStatusError(
          res.status,
          res.statusText,
          apiUrl.href,
          json,
          errorResponseBody,
          { metrics },
          attempt > 0 ? attempt : undefined,
          includeBodyInErrors
        );
      }

      // Handle non-streaming response
      if (!api.stream) {
        const resJson = await res.json();

        if (verbose) {
          console.log(
            '\n--- [AxAI API Response] ---\n',
            `Status: ${res.status} ${res.statusText}\n`,
            `Body:`,
            JSON.stringify(resJson, null, 2),
            '\n-------------------------\n'
          );
        }

        // Validate response if validator is provided
        if (api.validateResponse) {
          const isValid = await api.validateResponse(resJson);
          if (!isValid) {
            throw new AxAIServiceResponseError(
              'Invalid response data',
              apiUrl.href,
              json,
              { validation: 'response' },
              includeBodyInErrors
            );
          }
        }

        api.span?.setAttributes({
          'response.time': Date.now() - metrics.startTime,
          'response.retries': metrics.retryCount,
        });

        return resJson as TResponse;
      }

      if (verbose) {
        console.log(
          '\n--- [AxAI API Streaming Response Started] ---\n',
          `Status: ${res.status} ${res.statusText}\n`,
          '\n-------------------------------------------\n'
        );
      }

      // Handle streaming response
      if (!res.body) {
        throw new AxAIServiceResponseError(
          'Response body is null',
          apiUrl.href,
          json,
          { metrics },
          includeBodyInErrors
        );
      }

      let lastChunk: TResponse | undefined;
      let chunkCount = 0;

      // Flag to track if the controller is closed.
      let closed = false;
      let streamReader: ReadableStreamDefaultReader<TResponse> | undefined;

      // Enhanced wrapped stream
      // The listener must stay attached while the stream is consumed (a user
      // abort during streaming still needs to reach the fetch signal), so
      // ownership transfers to the stream teardown below.
      abortHandlerOwnedByStream = true;
      return new ReadableStream<TResponse>({
        start(controller) {
          const reader = res
            .body!.pipeThrough(new textDecoderStream())
            .pipeThrough(
              new SSEParser<TResponse>({
                // Ax accepts a final provider event without the SSE blank-line
                // terminator. Keep that compatibility behavior identical in
                // browsers and server runtimes.
                emitIncompleteEventOnEof: true,
                onEvent(chunk, event) {
                  lastChunk = chunk;
                  chunkCount++;
                  metrics.streamChunks = chunkCount;
                  metrics.lastChunkTime = Date.now();

                  api.span?.addEvent('stream.chunk', {
                    'stream.chunks': chunkCount,
                    'stream.duration': Date.now() - metrics.startTime,
                    'response.retries': metrics.retryCount,
                    'sse.event.type': event.event,
                  });
                },
              })
            )
            .getReader();
          streamReader = reader;

          async function read() {
            try {
              while (true) {
                const { done, value } = await reader.read();
                if (done) {
                  if (!closed) {
                    closed = true;
                    controller.close();
                  }
                  break;
                }

                // Check if the controller is already closed before enqueuing.
                if (closed) break;
                controller.enqueue(value);
              }
            } catch (e) {
              if (closed) return;

              const error = e as Error;
              const streamMetrics = {
                ...metrics,
                streamDuration: Date.now() - metrics.startTime,
              };

              if (
                error.name === 'AbortError' ||
                error.message?.includes('aborted')
              ) {
                controller.error(
                  api.abortSignal?.aborted
                    ? new AxAIServiceAbortedError(
                        apiUrl.href,
                        api.abortSignal.reason,
                        json,
                        { streamMetrics },
                        includeBodyInErrors
                      )
                    : new AxAIServiceStreamTerminatedError(
                        apiUrl.href,
                        json,
                        lastChunk,
                        { streamMetrics },
                        includeBodyInErrors
                      )
                );
              } else if (
                error instanceof TypeError &&
                error.message.includes('cancelled')
              ) {
                controller.error(
                  new AxAIServiceStreamTerminatedError(
                    apiUrl.href,
                    json,
                    lastChunk,
                    {
                      streamMetrics,
                      cancelReason: 'Stream cancelled by client',
                    },
                    includeBodyInErrors
                  )
                );
              } else {
                controller.error(
                  new AxAIServiceNetworkError(
                    error,
                    apiUrl.href,
                    json,
                    '[ReadableStream - consumed during streaming]',
                    {
                      streamMetrics,
                    },
                    includeBodyInErrors
                  )
                );
              }
            } finally {
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              removeUserAbortHandler?.();
              reader.releaseLock();
              if (streamReader === reader) streamReader = undefined;
            }
          }

          read();
        },
        // When the consumer cancels the stream, set our flag to stop processing further.
        cancel(reason) {
          closed = true;
          removeUserAbortHandler?.();
          return streamReader?.cancel(reason);
        },
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Check if this was a user abort or timeout
        if (api.abortSignal?.aborted) {
          throw new AxAIServiceAbortedError(
            apiUrl.href,
            api.abortSignal.reason,
            json,
            { metrics },
            includeBodyInErrors
          );
        }
        throw new AxAIServiceTimeoutError(
          apiUrl.href,
          timeoutMs || 0,
          json,
          { metrics },
          includeBodyInErrors
        );
      }

      // Wrap raw network errors from fetch() in AxAIServiceNetworkError
      // This ensures errors like TLS connection failures, DNS errors, etc. are retried
      let wrappedError: Error = error as Error;
      if (!(error instanceof AxAIServiceError) && error instanceof Error) {
        wrappedError = new AxAIServiceNetworkError(
          error,
          apiUrl.href,
          json,
          undefined,
          { metrics },
          includeBodyInErrors
        );
      }

      if (api.span?.isRecording()) {
        api.span.recordException(wrappedError);
        api.span.setAttributes({
          'error.time': Date.now() - metrics.startTime,
          'error.retries': metrics.retryCount,
        });
      }

      // Handle retryable network errors
      if (
        wrappedError instanceof AxAIServiceNetworkError &&
        shouldRetry(wrappedError, undefined, attempt, retryConfig)
      ) {
        const delay = calculateRetryDelay(attempt, retryConfig);
        attempt++;
        updateRetryMetrics(metrics);

        api.span?.addEvent('retry', {
          attempt,
          delay,
          error: wrappedError.message,
          'metrics.startTime': metrics.startTime,
          'metrics.retryCount': metrics.retryCount,
          'metrics.lastRetryTime': metrics.lastRetryTime,
        });

        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      if (wrappedError instanceof AxAIServiceError) {
        wrappedError.context.metrics = metrics;
      }

      throw wrappedError;
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      // Detach the user abort listener on every non-streaming exit path
      // (success, non-abort error, and each retry `continue`). Without this the
      // `{ once: true }` listener registered above only ever detaches on an
      // actual abort, so a long-lived caller signal accumulates one listener
      // per attempt (MaxListenersExceededWarning at 11, then unbounded). For a
      // streamed response the stream teardown owns the listener instead.
      if (!abortHandlerOwnedByStream) {
        removeUserAbortHandler?.();
      }
    }
  }
};

export function createApiConfig(
  config: Readonly<Partial<AxAPIConfig>>
): AxAPIConfig {
  return {
    retry: defaultRetryConfig,
    ...config,
    url: config.url!, // URL is required
  };
}
